/**
 * @fileoverview Animated supply/return pipe network.
 *
 * Renders the riser, per-floor distribution lines and radiator stubs. Stroke
 * width is proportional to the flow carried by each segment and the dash
 * animation speed follows the flow — the learner literally watches flow
 * redistribute when any valve changes.
 *
 * @module HydraulicBalancingTesting/components/PipeNetwork
 */

import React from 'react';

import { SVG_DIM, COLORS, BUILDING } from '../constants';
import { radiatorLayout } from './RoomUnit';

import styles from '../HydraulicBalancingTesting.module.css';

const { ROOM_WIDTH, ROOM_HEIGHT, MARGIN_LEFT, GROUND_Y } = SVG_DIM;

/** X of the supply riser */
const X_SUPPLY = MARGIN_LEFT - 38;
/** X of the return riser */
const X_RETURN = MARGIN_LEFT - 20;

/** Supply distribution line Y for a floor */
const ySupply = (floor) => GROUND_Y - ROOM_HEIGHT * floor - 9;
/** Return distribution line Y for a floor */
const yReturn = (floor) => GROUND_Y - ROOM_HEIGHT * floor - 4;

/** Radiator stub X positions (uses RoomUnit's shared radiator layout) */
const radiatorStubX = (room) => {
  const { totalW } = radiatorLayout(room.radiatorNominal);
  const roomX = MARGIN_LEFT + ROOM_WIDTH * room.col;
  const radX = roomX + ROOM_WIDTH - 18 - totalW;
  return { supply: radX + 3, return: radX + totalW - 3 };
};

/** Stroke width from carried flow [m³/s] */
const widthFor = (flow) => Math.min(1.2 + flow * 9000, 7);

/** Dash animation duration from carried flow; null = no animation */
const durationFor = (flow) => {
  if (flow < 2e-6) return null;
  return Math.max(0.35, Math.min(3.5, 1.1e-3 / flow));
};

/** Return-water colour shifts from blue (cool) to red-ish (hot return = bad) */
const returnColor = (tReturn) => {
  const t = Math.max(0, Math.min(1, (tReturn - 28) / 17));
  const r = Math.round(70 + t * (226 - 70));
  const g = Math.round(130 - t * 60);
  const b = Math.round(180 - t * 100);
  return `rgb(${r},${g},${b})`;
};

/** One animated pipe segment (line). */
const Pipe = ({ x1, y1, x2, y2, flow, color }) => {
  const dur = durationFor(flow);
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={color}
      strokeWidth={widthFor(flow)}
      strokeLinecap="round"
      strokeDasharray={dur ? '7 5' : undefined}
      className={dur ? styles.flowPipe : undefined}
      style={dur ? { animationDuration: `${dur}s` } : undefined}
      opacity={dur ? 1 : 0.35}
    />
  );
};

/**
 * @param {Object} props
 * @param {Array<Object>} props.rooms - 9 rooms (floor-major)
 * @param {Object} props.network - solveNetwork result
 * @param {number} props.tReturn - mixed return temperature [°C]
 */
export const PipeNetwork = React.memo(({ rooms, network, tReturn }) => {
  const retColor = returnColor(tReturn);
  const segments = [];

  // --- riser segments (pump → floor 0 tee → floor 1 tee → floor 2 tee)
  let prevYS = GROUND_Y - 14;
  let prevYR = GROUND_Y - 14;
  for (let f = 0; f < BUILDING.FLOORS; f++) {
    const carried = network.riserFlows[f];
    segments.push(
      <Pipe key={`rs${f}`} x1={X_SUPPLY} y1={prevYS} x2={X_SUPPLY} y2={ySupply(f)} flow={carried} color={COLORS.SUPPLY} />,
      <Pipe key={`rr${f}`} x1={X_RETURN} y1={yReturn(f)} x2={X_RETURN} y2={prevYR} flow={carried} color={retColor} />
    );
    prevYS = ySupply(f);
    prevYR = yReturn(f);
  }

  // --- per-floor distribution lines + radiator stubs
  for (let f = 0; f < BUILDING.FLOORS; f++) {
    const yS = ySupply(f);
    const yR = yReturn(f);
    const floorRooms = rooms.filter((r) => r.floor === f);

    let xPrevS = X_SUPPLY;
    let xPrevR = X_RETURN;
    for (let c = 0; c < BUILDING.COLUMNS; c++) {
      const room = floorRooms[c];
      const idx = f * BUILDING.COLUMNS + c;
      // flow carried by the segment leading to this column = Σ flows of cols ≥ c
      let carried = 0;
      for (let cc = c; cc < BUILDING.COLUMNS; cc++) {
        carried += network.roomFlows[f * BUILDING.COLUMNS + cc];
      }
      const stubs = radiatorStubX(room);
      segments.push(
        <Pipe key={`fs${f}-${c}`} x1={xPrevS} y1={yS} x2={stubs.supply} y2={yS} flow={carried} color={COLORS.SUPPLY} />,
        <Pipe key={`fr${f}-${c}`} x1={stubs.return} y1={yR} x2={xPrevR} y2={yR} flow={carried} color={retColor} />
      );
      // radiator stubs (thin)
      const roomY = GROUND_Y - ROOM_HEIGHT * (f + 1);
      const radBottom = roomY + ROOM_HEIGHT - 12;
      segments.push(
        <Pipe key={`ss${idx}`} x1={stubs.supply} y1={yS} x2={stubs.supply} y2={radBottom} flow={network.roomFlows[idx]} color={COLORS.SUPPLY} />,
        <Pipe key={`sr${idx}`} x1={stubs.return} y1={radBottom} x2={stubs.return} y2={yR} flow={network.roomFlows[idx]} color={retColor} />
      );
      xPrevS = stubs.supply;
      xPrevR = stubs.return;
    }
  }

  return (
    <g>
      {segments}
      {/* pump symbol on the supply riser */}
      <g>
        <circle cx={X_SUPPLY} cy={GROUND_Y - 14} r={9} fill="white" stroke={COLORS.PRIMARY} strokeWidth={2} />
        <polygon
          points={`${X_SUPPLY - 3},${GROUND_Y - 19} ${X_SUPPLY + 5},${GROUND_Y - 14} ${X_SUPPLY - 3},${GROUND_Y - 9}`}
          fill={COLORS.PRIMARY}
        />
        <title>Circulation pump</title>
      </g>
    </g>
  );
});

PipeNetwork.displayName = 'PipeNetwork';
